import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "./Layout";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  UserCheck,
  Eye,
  Trash2,
  Download,
  Calendar,
  Filter
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExecutivesPage = ({ user, onLogout }) => {
  const [executives, setExecutives] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [executiveToDelete, setExecutiveToDelete] = useState(null);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    role: "executive",
    status: "active",
    hotel_ids: []
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Report download states
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    status: "",
    start_date: "",
    end_date: ""
  });
  const [downloading, setDownloading] = useState(false);

  const fetchExecutives = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/executives`);
      setExecutives(response.data);
    } catch (error) {
      console.error("Error fetching executives:", error);
      toast.error("Failed to fetch executives");
    } finally {
      setLoading(false);
    }
  };

  const fetchHotels = async () => {
    try {
      const response = await axios.get(`${API}/hotels`);
      setHotels(response.data);
    } catch (error) {
      console.error("Error fetching hotels:", error);
      toast.error("Failed to fetch hotels");
    }
  };

  useEffect(() => {
    fetchExecutives();
    fetchHotels();
  }, []);

  const filteredExecutives = executives.filter(executive =>
    executive.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    executive.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    executive.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      username: "",
      password: "",
      role: "executive",
      status: "active",
      hotel_ids: []
    });
    setErrors({});
  };

  const validateForm = (isEdit = false) => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!isEdit && !formData.password.trim()) newErrors.password = "Password is required";
    
    // Validate email format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleHotelSelection = (hotelId, checked) => {
    setFormData(prev => ({
      ...prev,
      hotel_ids: checked
        ? [...prev.hotel_ids, hotelId]
        : prev.hotel_ids.filter(id => id !== hotelId)
    }));
  };

  const handleAddExecutive = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/executives`, formData);
      toast.success("Executive added successfully");
      setShowAddDialog(false);
      resetForm();
      fetchExecutives();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to add executive";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditExecutive = async () => {
    if (!validateForm(true)) return;
    
    setSubmitting(true);
    try {
      // Don't send password if it's empty
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await axios.put(`${API}/executives/${selectedExecutive.id}`, updateData);
      toast.success("Executive updated successfully");
      setShowEditDialog(false);
      resetForm();
      setSelectedExecutive(null);
      fetchExecutives();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to update executive";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (executive) => {
    setSelectedExecutive(executive);
    setFormData({
      name: executive.name,
      email: executive.email,
      username: executive.username,
      password: "", // Don't pre-fill password for security
      role: executive.role,
      status: executive.status,
      hotel_ids: executive.hotel_ids || []
    });
    setShowEditDialog(true);
  };

  const openViewDialog = (executive) => {
    setSelectedExecutive(executive);
    setShowViewDialog(true);
  };

  const openDeleteDialog = (executive) => {
    setExecutiveToDelete(executive);
    setShowDeleteDialog(true);
  };

  const handleDeleteExecutive = async () => {
    if (!executiveToDelete) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/executives/${executiveToDelete.id}`);
      toast.success("Executive deleted successfully");
      setShowDeleteDialog(false);
      setExecutiveToDelete(null);
      fetchExecutives();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to delete executive";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    return status === "active" ? "status-badge status-active" : "status-badge status-inactive";
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case "admin": return "status-badge status-qualified";
      case "manager": return "status-badge status-active";
      case "executive": return "status-badge status-contacted";
      default: return "status-badge status-contacted";
    }
  };

  const getHotelNames = (hotelIds) => {
    if (!hotelIds || hotelIds.length === 0) return "None";
    return hotelIds
      .map(id => {
        const hotel = hotels.find(h => h.id === id);
        return hotel ? hotel.hotel_name : "Unknown";
      })
      .join(", ");
  };

  const handleDownloadReport = async (format) => {
    setDownloading(true);
    try {
      const formData = new FormData();
      formData.append("format", format);
      if (reportFilters.status) formData.append("status", reportFilters.status);
      if (reportFilters.start_date) formData.append("start_date", reportFilters.start_date);
      if (reportFilters.end_date) formData.append("end_date", reportFilters.end_date);

      const response = await axios.post(`${API}/executive/visits/export`, formData, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `mis_visitas_${new Date().toISOString().split('T')[0]}.${format}`;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Reporte descargado exitosamente");
      setShowReportDialog(false);
    } catch (error) {
      console.error("Error downloading report:", error);
      const errorMessage = error.response?.data?.detail || "Error al descargar el reporte";
      toast.error(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const resetReportFilters = () => {
    setReportFilters({
      status: "",
      start_date: "",
      end_date: ""
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h2 className="page-title">Executives Management</h2>
        <p className="page-description">
          Manage executive accounts and their hotel assignments
        </p>
      </div>

      {/* Executive Reports Section - Only for executive users */}
      {user && user.role === "executive" && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <div className="card-header">
            <h3 className="card-title">
              <Download size={20} style={{ marginRight: "0.5rem" }} />
              Descarga de Reportes de Visitas
            </h3>
          </div>
          <div className="card-content">
            <p style={{ marginBottom: "1rem", color: "#6b7280" }}>
              Descarga tus reportes de visitas en formato Excel, CSV o PDF
            </p>
            <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  resetReportFilters();
                  setShowReportDialog(true);
                }}>
                  <Download size={20} style={{ marginRight: "0.5rem" }} />
                  Generar Reporte
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Descargar Reporte de Visitas</DialogTitle>
                </DialogHeader>
                <ReportFiltersForm
                  filters={reportFilters}
                  onFilterChange={setReportFilters}
                  onDownload={handleDownloadReport}
                  onCancel={() => setShowReportDialog(false)}
                  downloading={downloading}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      )}

      {/* Search and Actions - Admin and Manager users */}
      {user && (user.role === "admin" || user.role === "manager") && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <div className="card-content">
            <div className="search-filters">
              <div className="search-input">
                <div style={{ position: "relative" }}>
                  <Search 
                    size={20} 
                    style={{ 
                      position: "absolute", 
                      left: "0.75rem", 
                      top: "50%", 
                      transform: "translateY(-50%)", 
                      color: "#6b7280" 
                    }} 
                  />
                  <Input
                    placeholder="Search executives by name, email, or username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: "2.5rem" }}
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Add Executive button - Only for admin */}
              {user.role === "admin" && (
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={() => {
                        resetForm();
                        setShowAddDialog(true);
                      }}
                      data-testid="add-executive-button"
                    >
                      <Plus size={20} style={{ marginRight: "0.5rem" }} />
                      Add Executive
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Add New Executive</DialogTitle>
                    </DialogHeader>
                    <ExecutiveForm
                      formData={formData}
                      errors={errors}
                      hotels={hotels}
                      onInputChange={handleInputChange}
                      onHotelSelection={handleHotelSelection}
                      onSubmit={handleAddExecutive}
                      onCancel={() => setShowAddDialog(false)}
                      submitting={submitting}
                      isEdit={false}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Executives Table - Admin, manager, or executive profile */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {user && user.role === "admin" 
              ? `Executives (${filteredExecutives.length})`
              : user && user.role === "manager"
              ? `Mi Equipo (${filteredExecutives.length})`
              : "Mi Perfil"
            }
          </h3>
        </div>
        <div className="card-content" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <div className="loading-spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "1rem", color: "#6b7280" }}>Loading...</p>
            </div>
          ) : user && user.role === "executive" ? (
            // Executive profile view
            <div style={{ padding: "2rem" }}>
              <ExecutiveProfile 
                executive={user} 
                hotels={hotels}
              />
            </div>
          ) : filteredExecutives.length === 0 ? (
            <div className="empty-state">
              <UserCheck className="empty-state-icon" />
              <h3 className="empty-state-title">No executives found</h3>
              <p className="empty-state-description">
                {searchTerm
                  ? "Try adjusting your search term"
                  : user && user.role === "admin" 
                    ? "Get started by adding your first executive"
                    : "No executives in your assigned hotels"
                }
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Hotels</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExecutives.map((executive) => (
                    <tr key={executive.id}>
                      <td style={{ fontWeight: "600" }}>{executive.name}</td>
                      <td>{executive.username}</td>
                      <td>{executive.email}</td>
                      <td>
                        <span className={getRoleBadgeClass(executive.role)}>
                          {executive.role === 'admin' ? 'Administrador' :
                           executive.role === 'manager' ? 'Manager' : 'Ejecutivo'}
                        </span>
                      </td>
                      <td>
                        <span className={getStatusBadgeClass(executive.status)}>
                          {executive.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {getHotelNames(executive.hotel_ids)}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => openViewDialog(executive)}
                            className="btn-icon"
                            title="View details"
                            data-testid={`view-executive-${executive.id}`}
                          >
                            <Eye size={16} />
                          </button>
                          {user && user.role === "admin" && (
                            <>
                              <button
                                onClick={() => openEditDialog(executive)}
                                className="btn-icon edit"
                                title="Edit executive"
                                data-testid={`edit-executive-${executive.id}`}
                              >
                                <Edit size={16} />
                              </button>
                              {executive.username !== "admin" && (
                                <button
                                  onClick={() => openDeleteDialog(executive)}
                                  className="btn-icon delete"
                                  title="Delete executive"
                                  data-testid={`delete-executive-${executive.id}`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Admin-only dialogs */}
      {user && user.role === "admin" && (
        <>
          {/* Edit Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Executive</DialogTitle>
              </DialogHeader>
              <ExecutiveForm
                formData={formData}
                errors={errors}
                hotels={hotels}
                onInputChange={handleInputChange}
                onHotelSelection={handleHotelSelection}
                onSubmit={handleEditExecutive}
                onCancel={() => {
                  setShowEditDialog(false);
                  resetForm();
                  setSelectedExecutive(null);
                }}
                submitting={submitting}
                isEdit={true}
              />
            </DialogContent>
          </Dialog>

          {/* View Dialog */}
          {selectedExecutive && (
            <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Executive Details</DialogTitle>
                </DialogHeader>
                <ExecutiveDetails 
                  executive={selectedExecutive} 
                  hotels={hotels}
                  onClose={() => setShowViewDialog(false)}
                />
              </DialogContent>
            </Dialog>
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Executive</DialogTitle>
              </DialogHeader>
              <div>
                <p style={{ marginBottom: "1.5rem" }}>
                  Are you sure you want to delete <strong>{executiveToDelete?.name}</strong>? 
                  This action cannot be undone and will only work if the executive has no associated clients.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDeleteDialog(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleDeleteExecutive}
                    disabled={submitting}
                    data-testid="confirm-delete-executive"
                  >
                    {submitting ? (
                      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <div className="loading-spinner"></div>
                        Deleting...
                      </span>
                    ) : (
                      "Delete Executive"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Layout>
  );
};

// Executive Form Component
const ExecutiveForm = ({ 
  formData, 
  errors, 
  hotels,
  onInputChange, 
  onHotelSelection,
  onSubmit, 
  onCancel, 
  submitting, 
  isEdit 
}) => (
  <div>
    <div className="form-grid">
      <div>
        <Label htmlFor="name">Full Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => onInputChange("name", e.target.value)}
          className={errors.name ? "error" : ""}
          placeholder="e.g., John Doe"
          data-testid="name-input"
        />
        {errors.name && <div className="error-message">{errors.name}</div>}
      </div>

      <div>
        <Label htmlFor="username">Username *</Label>
        <Input
          id="username"
          value={formData.username}
          onChange={(e) => onInputChange("username", e.target.value)}
          className={errors.username ? "error" : ""}
          placeholder="e.g., johndoe"
          data-testid="username-input"
        />
        {errors.username && <div className="error-message">{errors.username}</div>}
      </div>

      <div className="form-grid-full">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => onInputChange("email", e.target.value)}
          className={errors.email ? "error" : ""}
          placeholder="e.g., john@company.com"
          data-testid="email-input"
        />
        {errors.email && <div className="error-message">{errors.email}</div>}
      </div>

      <div>
        <Label htmlFor="password">
          Password {!isEdit && "*"} {isEdit && "(leave empty to keep current)"}
        </Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => onInputChange("password", e.target.value)}
          className={errors.password ? "error" : ""}
          placeholder={isEdit ? "Enter new password" : "Enter password"}
          data-testid="password-input"
        />
        {errors.password && <div className="error-message">{errors.password}</div>}
      </div>

      <div>
        <Label htmlFor="role">Role</Label>
        <Select 
          value={formData.role} 
          onValueChange={(value) => onInputChange("role", value)}
        >
          <SelectTrigger data-testid="role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="executive">Ejecutivo</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="admin">Administrador</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select 
          value={formData.status} 
          onValueChange={(value) => onInputChange("status", value)}
        >
          <SelectTrigger data-testid="status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="form-grid-full">
        <Label>Assigned Hotels</Label>
        <div style={{ 
          maxHeight: "200px", 
          overflowY: "auto", 
          border: "1px solid #e5e7eb", 
          borderRadius: "6px", 
          padding: "1rem",
          backgroundColor: "#f9fafb"
        }}>
          {hotels.length === 0 ? (
            <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>No hotels available</p>
          ) : (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {hotels.map((hotel) => (
                <div key={hotel.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Checkbox
                    id={`hotel-${hotel.id}`}
                    checked={formData.hotel_ids.includes(hotel.id)}
                    onCheckedChange={(checked) => onHotelSelection(hotel.id, checked)}
                    data-testid={`hotel-checkbox-${hotel.id}`}
                  />
                  <Label 
                    htmlFor={`hotel-${hotel.id}`}
                    style={{ fontSize: "0.875rem", cursor: "pointer" }}
                  >
                    {hotel.hotel_name} ({hotel.hotel_code})
                  </Label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

    <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
      <Button variant="outline" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={onSubmit} disabled={submitting} data-testid="submit-executive-button">
        {submitting ? (
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div className="loading-spinner"></div>
            {isEdit ? "Updating..." : "Adding..."}
          </span>
        ) : (
          isEdit ? "Update Executive" : "Add Executive"
        )}
      </Button>
    </div>
  </div>
);

// Executive Profile Component (for executive users)
const ExecutiveProfile = ({ executive, hotels }) => {
  const getHotelNames = (hotelIds) => {
    if (!hotelIds || hotelIds.length === 0) return "Ningún hotel asignado";
    return hotelIds
      .map(id => {
        const hotel = hotels.find(h => h.id === id);
        return hotel ? `${hotel.hotel_name} (${hotel.hotel_code})` : "Desconocido";
      })
      .join(", ");
  };

  return (
    <div>
      <div style={{ display: "grid", gap: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Nombre Completo</Label>
            <div style={{ padding: "0.75rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem", fontWeight: "600" }}>
              {executive.name}
            </div>
          </div>
          <div>
            <Label>Nombre de Usuario</Label>
            <div style={{ padding: "0.75rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {executive.username}
            </div>
          </div>
        </div>

        <div>
          <Label>Correo Electrónico</Label>
          <div style={{ padding: "0.75rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {executive.email}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Rol</Label>
            <div style={{ marginTop: "0.25rem" }}>
              <span className="status-badge status-contacted">
                Ejecutivo
              </span>
            </div>
          </div>
          <div>
            <Label>Estado</Label>
            <div style={{ marginTop: "0.25rem" }}>
              <span className={`status-badge ${executive.status === "active" ? "status-active" : "status-inactive"}`}>
                {executive.status === "active" ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>
        </div>

        <div>
          <Label>Hoteles Asignados</Label>
          <div style={{ padding: "0.75rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem", minHeight: "3rem" }}>
            {getHotelNames(executive.hotel_ids)}
          </div>
        </div>

        <div>
          <Label>Cuenta Creada</Label>
          <div style={{ padding: "0.75rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {new Date(executive.created_at).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Report Filters Form Component
const ReportFiltersForm = ({ filters, onFilterChange, onDownload, onCancel, downloading }) => (
  <div>
    <div style={{ display: "grid", gap: "1rem", marginBottom: "1.5rem" }}>
      <div>
        <Label htmlFor="status">Estado de la Visita</Label>
        <Select 
          value={filters.status} 
          onValueChange={(value) => onFilterChange({ ...filters, status: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">Todos los estados</SelectItem>
            <SelectItem value="scheduled">Programada</SelectItem>
            <SelectItem value="in_progress">En Progreso</SelectItem>
            <SelectItem value="completed">Completada</SelectItem>
            <SelectItem value="cancelled">Cancelada</SelectItem>
            <SelectItem value="rescheduled">Reprogramada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="start_date">Fecha de Inicio</Label>
        <Input
          id="start_date"
          type="date"
          value={filters.start_date}
          onChange={(e) => onFilterChange({ ...filters, start_date: e.target.value })}
        />
      </div>

      <div>
        <Label htmlFor="end_date">Fecha de Fin</Label>
        <Input
          id="end_date"
          type="date"
          value={filters.end_date}
          onChange={(e) => onFilterChange({ ...filters, end_date: e.target.value })}
        />
      </div>
    </div>

    <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "1rem" }}>
      <p style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "1rem" }}>
        Selecciona el formato de descarga:
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <Button
          onClick={() => onDownload("xlsx")}
          disabled={downloading}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "1rem", height: "auto" }}
        >
          {downloading ? (
            <div className="loading-spinner" style={{ marginBottom: "0.25rem" }}></div>
          ) : (
            <Download size={20} style={{ marginBottom: "0.25rem" }} />
          )}
          <span style={{ fontSize: "0.75rem" }}>Excel</span>
        </Button>
        
        <Button
          onClick={() => onDownload("csv")}
          disabled={downloading}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "1rem", height: "auto" }}
        >
          {downloading ? (
            <div className="loading-spinner" style={{ marginBottom: "0.25rem" }}></div>
          ) : (
            <Download size={20} style={{ marginBottom: "0.25rem" }} />
          )}
          <span style={{ fontSize: "0.75rem" }}>CSV</span>
        </Button>
        
        <Button
          onClick={() => onDownload("pdf")}
          disabled={downloading}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "1rem", height: "auto" }}
        >
          {downloading ? (
            <div className="loading-spinner" style={{ marginBottom: "0.25rem" }}></div>
          ) : (
            <Download size={20} style={{ marginBottom: "0.25rem" }} />
          )}
          <span style={{ fontSize: "0.75rem" }}>PDF</span>
        </Button>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="outline" onClick={onCancel} disabled={downloading}>
          Cancelar
        </Button>
      </div>
    </div>
  </div>
);

// Executive Details Component
const ExecutiveDetails = ({ executive, hotels, onClose }) => {
  const getHotelNames = (hotelIds) => {
    if (!hotelIds || hotelIds.length === 0) return "None assigned";
    return hotelIds
      .map(id => {
        const hotel = hotels.find(h => h.id === id);
        return hotel ? `${hotel.hotel_name} (${hotel.hotel_code})` : "Unknown";
      })
      .join(", ");
  };

  return (
    <div>
      <div style={{ display: "grid", gap: "1rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Full Name</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {executive.name}
            </div>
          </div>
          <div>
            <Label>Username</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {executive.username}
            </div>
          </div>
        </div>

        <div>
          <Label>Email Address</Label>
          <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {executive.email}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Role</Label>
            <div style={{ marginTop: "0.25rem" }}>
              <span className={`status-badge ${executive.role === "admin" ? "status-qualified" : "status-contacted"}`}>
                {executive.role}
              </span>
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <div style={{ marginTop: "0.25rem" }}>
              <span className={`status-badge ${executive.status === "active" ? "status-active" : "status-inactive"}`}>
                {executive.status}
              </span>
            </div>
          </div>
        </div>

        <div>
          <Label>Assigned Hotels</Label>
          <div style={{ padding: "0.75rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {getHotelNames(executive.hotel_ids)}
          </div>
        </div>

        <div>
          <Label>Account Created</Label>
          <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {new Date(executive.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
};

export default ExecutivesPage;